"use client";

/**
 * 상품 상세 컴포넌트
 *
 * 디자인: faire.com / 29cm — 각진 버튼, 정제된 간격, 선택 블록
 * 옵션: 다중 조합 지원 (컬러 A 5개 + 컬러 B 3개)
 */
import { useMemo, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight, Heart, Minus, Plus, X } from "lucide-react";
import { toast } from "sonner";

import { trackAddToCart } from "@/shared/analytics";
import {
  createProductService,
  type BrandNotice,
  type ProductListItem,
} from "@/shared/api/services/productService";
import { useAuth, useBuyerService } from "@/shared/auth/hooks";
import { cn } from "@/lib/utils";

import { brandKeys } from "@/features/brand/queries/keys";
import { useCarts } from "@/features/cart/queries/useCart";
import { useProductFlashSaleInfo } from "@/features/flash-sale";
import { OptionSelect } from "@/features/shopping/components/product-detail/OptionSelect";
import { ProductInfoTabs } from "@/features/shopping/components/product-detail/ProductInfoTabs";
import { ProductCard } from "@/features/shopping/components/ProductCard";
import { normalizeProductOptions } from "@/features/shopping/hooks/productOptionNormalization";
import {
  useProductOptions,
  type SelectedOptionItem,
} from "@/features/shopping/hooks/useProductOptions";
import {
  useAddToCart,
  useCheckFavorite,
  useProductDetail,
  useToggleFavoriteDetail,
} from "@/features/shopping/queries/useProductDetail";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ImageGallery } from "@/components/ui/image-gallery";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";

interface ProductDetailProps {
  productId: string;
}

function ProductDetailSkeleton() {
  return (
    <div className="max-w-screen-xl mx-auto px-4 py-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
        <Skeleton className="aspect-square w-full" />
        <div className="flex flex-col gap-4">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-7 w-3/4" />
          <Skeleton className="h-5 w-40" />
          <Skeleton className="h-px w-full mt-4" />
          <Skeleton className="h-10 w-full mt-4" />
          <Skeleton className="h-12 w-full" />
        </div>
      </div>
    </div>
  );
}

export function ProductDetail({ productId }: ProductDetailProps) {
  const { isAuthenticated, user } = useAuth();
  const router = useRouter();
  const { data, isLoading, isError } = useProductDetail(productId);
  const toggleFavorite = useToggleFavoriteDetail();
  const addToCart = useAddToCart();
  const favoriteQuery = useCheckFavorite(productId);
  const isFavorited = favoriteQuery.data ?? false;
  const [heartSpring, setHeartSpring] = useState(false);
  const [contentExpanded, setContentExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState("상품정보");
  const [cartDialogOpen, setCartDialogOpen] = useState(false);
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);
  const [onlineBlockDialogOpen, setOnlineBlockDialogOpen] = useState(false);

  const cartsQuery = useCarts();
  const service = useBuyerService(createProductService);
  const detail = data?.data;
  const product = detail?.product;
  const brandId = product?.idBrand ? String(product.idBrand) : "";
  const flashSaleInfo = useProductFlashSaleInfo(product?.id);

  // 기존 장바구니 수량 중 MOQ 기준에 포함되는 수량.
  // 레거시 기준: 추가옵션 수량은 최소주문수량을 채우지 못하고,
  // 본상품(idOptionDetail=0) 또는 필수 옵션 조합만 MOQ에 포함된다.
  const existingCartQuantity = useMemo(() => {
    const cartEntries = cartsQuery.data?.data;
    if (!cartEntries || !product) return 0;
    const productEntries = cartEntries.filter((entry) => entry.cart.idProduct === product.id);
    const options = detail?.options ?? [];
    const optionDetails = detail?.options_details ?? [];
    const normalizedOptions = normalizeProductOptions(options, optionDetails);
    const hasRequiredOptions = normalizedOptions.some((option) => option.isRequire === 1);
    const hasOptionalOptions = normalizedOptions.some((option) => option.isRequire === 0);
    const hasOptions =
      options.length > 0 && (product.is_exist_options === "Y" || hasRequiredOptions);

    if (!hasOptions) {
      return productEntries.reduce((sum, entry) => sum + entry.cart.goodsCount, 0);
    }
    if (!hasRequiredOptions && hasOptionalOptions) {
      return productEntries
        .filter((entry) => entry.cart.idOptionDetail === 0)
        .reduce((sum, entry) => sum + entry.cart.goodsCount, 0);
    }

    const requiredDetailIds = new Set(
      optionDetails.filter((detail) => detail.is_require === 1).map((detail) => detail.id),
    );
    return cartEntries
      .filter(
        (entry) =>
          entry.cart.idProduct === product.id && requiredDetailIds.has(entry.cart.idOptionDetail),
      )
      .reduce((sum, entry) => sum + entry.cart.goodsCount, 0);
  }, [cartsQuery.data, detail, product]);

  const brandNoticeQuery = useQuery({
    queryKey: brandKeys.notice(brandId),
    queryFn: () => service.getBrandNotice(brandId),
    enabled: !!brandId,
  });
  const relatedQuery = useQuery({
    queryKey: brandKeys.products(brandId, productId),
    queryFn: () => service.getBrandProducts({ idBrand: brandId, page: 1, order: "popular" }),
    enabled: !!brandId,
  });

  const opt = useProductOptions({
    product: product ?? ({} as NonNullable<typeof product>),
    options: detail?.options ?? [],
    optionsDetails: detail?.options_details ?? [],
    existingCartQuantity,
  });

  if (isLoading) return <ProductDetailSkeleton />;
  if (isError || !detail || !product) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <p className="text-muted-foreground">상품 정보를 불러올 수 없습니다.</p>
      </div>
    );
  }

  const images = product.imagePath ? [product.imagePath] : [];

  // isOnlineSale 차단: userGroups === 'online'이고 isOnlineSale === 0
  const isOnlineBlocked = isAuthenticated && user?.userGroups === "online" && !product.isOnlineSale;

  // prodStatus !== "sale" 이면 구매 불가. 백엔드가 cart/order 단계에서 "Invalid Product Status"로 거절하므로
  // 프론트에서 버튼을 먼저 비활성화해 사용자 혼란을 막는다. 라벨은 레거시와 동일하게 통일.
  const unavailableLabel = product.prodStatus === "sale" ? null : "품절된 상품입니다";

  function handleAddToCart() {
    if (!product) return;
    if (isOnlineBlocked) {
      setOnlineBlockDialogOpen(true);
      return;
    }
    if (!opt.isMoqMet) {
      toast.error(`최소 주문 수량은 ${opt.minOrder}개입니다. (현재 ${opt.totalQuantity}개)`);
      return;
    }
    const cartInfo = opt.buildCartInfo();
    if (cartInfo.length === 0) return;
    addToCart.mutate(
      { directCart: "n", idProduct: product.id, cartInfo },
      {
        onSuccess: () => {
          setCartDialogOpen(true);
          trackAddToCart({
            userId: user?.id,
            item: {
              itemId: product.id,
              itemName: product.name,
              price: product.goodsPrice,
              quantity: opt.totalQuantity,
              brandId: product.idBrand ?? null,
              brandName: product["Brand.name"] ?? null,
            },
            source: "product_detail",
          });
        },
        onError: () => toast.error("장바구니 추가에 실패했습니다."),
      },
    );
  }

  function handleFavoriteToggle() {
    if (!product) return;
    if (!isAuthenticated) {
      setLoginDialogOpen(true);
      return;
    }
    setHeartSpring(true);
    setTimeout(() => setHeartSpring(false), 400);
    toggleFavorite.mutate(product.id);
  }

  function handleOptionalAdd(optionId: number, variantCode: string) {
    const result = opt.addOptionalItem(optionId, variantCode);
    if (result === "duplicate") toast.error("이미 선택된 옵션입니다.");
    else if (result === "soldout") toast.error("해당 옵션은 품절입니다.");
    else if (result === "invalid_price")
      toast.error("해당 옵션은 가격이 등록되어 있지 않아 주문할 수 없습니다.");
  }

  const brandNotice = brandNoticeQuery.data?.data as BrandNotice | undefined;
  const brandNoticeHtml = brandNotice?.content
    ? sanitizeBrandNoticeHtml(decodeHtmlEntities(brandNotice.content))
    : "";
  const relatedData = relatedQuery.data?.data?.list as ProductListItem[] | undefined;
  const relatedProducts = (relatedData ?? []).filter((p) => String(p.id) !== productId).slice(0, 5);

  return (
    <div className="max-w-screen-xl mx-auto px-4 py-8">
      {/* ── 상품 정보 ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
        <ImageGallery images={images} alt={product.name} />

        <div className="flex flex-col">
          {/* 브랜드 */}
          <Link
            href={`/brands/${product.idBrand}`}
            className="text-[13px] text-muted-foreground underline underline-offset-4 hover:text-foreground transition-colors"
          >
            {product["Brand.name"]}
          </Link>

          {/* 상품명 + 찜 */}
          <div className="flex items-start justify-between gap-4 mt-2">
            <h1 className="text-xl font-normal leading-snug">{product.name}</h1>
            <button
              type="button"
              onClick={handleFavoriteToggle}
              disabled={toggleFavorite.isPending}
              className="shrink-0 mt-0.5"
              aria-label="찜하기"
            >
              <Heart
                className={cn(
                  "h-6 w-6 transition-colors",
                  isFavorited
                    ? "fill-neutral-800 text-neutral-800"
                    : "text-neutral-300 hover:text-neutral-800",
                  heartSpring && "animate-heart-spring",
                )}
              />
            </button>
          </div>

          {/* 가격 */}
          <div className="mt-3">
            {isAuthenticated ? (
              flashSaleInfo ? (
                <div className="flex items-baseline gap-2">
                  <span className="text-xl font-bold tracking-tight">
                    {flashSaleInfo.discountedPrice.toLocaleString("ko-KR")}원
                  </span>
                  <span className="text-[13px] text-neutral-400 line-through">
                    {flashSaleInfo.originalPrice.toLocaleString("ko-KR")}원
                  </span>
                </div>
              ) : (
                <div className="flex items-baseline gap-2">
                  <span className="text-xl font-bold tracking-tight">
                    {product.goodsPrice.toLocaleString("ko-KR")}원
                  </span>
                  {product.fixedPrice > product.goodsPrice && (
                    <span className="text-[13px] text-neutral-400 line-through">
                      {product.fixedPrice.toLocaleString("ko-KR")}원
                    </span>
                  )}
                </div>
              )
            ) : (
              <p className="text-base text-neutral-500">판매가 사업자 회원 공개</p>
            )}
          </div>

          <Separator className="my-5" />

          {/* 상품 정보 */}
          <div className="space-y-2 text-[13px]">
            {opt.minOrder > 1 && (
              <div className="flex justify-between">
                <span className="font-medium">최소 주문 수량</span>
                <span>{opt.minOrder}개</span>
              </div>
            )}
            {product.fixedPrice > product.goodsPrice && (
              <div className="flex justify-between">
                <span className="font-medium">최저 준수 가격</span>
                {isAuthenticated ? (
                  <span>{product.fixedPrice.toLocaleString("ko-KR")}원</span>
                ) : (
                  <span className="text-muted-foreground">회원 공개</span>
                )}
              </div>
            )}
            <div className="flex justify-between">
              <span className="font-medium">온라인 판매 여부</span>
              <span className={product.isOnlineSale ? "" : "text-destructive"}>
                {product.isOnlineSale ? "판매 가능" : "판매 불가"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium">이미지 사용 여부</span>
              <span className={product.isImageUsage ? "" : "text-destructive"}>
                {product.isImageUsage ? "사용 가능" : "사용 불가"}
              </span>
            </div>
            {product.salesUnit > 1 && (
              <div className="flex justify-between">
                <span className="font-medium">묶음 판매 단위</span>
                <span>{product.salesUnit}개</span>
              </div>
            )}
          </div>

          {/* 옵션 있는 경우 */}
          {opt.hasOptions && (
            <div className="mt-5 space-y-4">
              {/* 추가옵션만 있는 경우: 본 상품 수량을 먼저 선택 (레거시 OnlyDefaultSelect 패턴) */}
              {opt.isOptionalOnly && (
                <div className="flex items-center justify-between">
                  <span className="text-[13px] text-muted-foreground">수량</span>
                  <div className="flex items-center">
                    <div className="flex items-center border border-border">
                      <button
                        type="button"
                        className="h-8 w-8 flex items-center justify-center text-muted-foreground hover:text-foreground"
                        onClick={() => opt.changeNoOptionQuantity(-1)}
                        disabled={opt.noOptionQuantity <= opt.noOptionMin}
                      >
                        <Minus className="h-3 w-3" />
                      </button>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={opt.noOptionQuantity}
                        onChange={(e) => {
                          const raw = e.target.value.replace(/[^0-9]/g, "");
                          if (raw === "") return;
                          opt.setNoOptionQuantityDirect(parseInt(raw, 10));
                        }}
                        onBlur={(e) => {
                          const raw = e.target.value.replace(/[^0-9]/g, "");
                          const parsed =
                            raw === ""
                              ? opt.noOptionMin
                              : Math.max(opt.noOptionMin, parseInt(raw, 10));
                          opt.setNoOptionQuantityDirect(parsed);
                        }}
                        className="w-10 h-8 text-center text-[13px] bg-transparent focus:outline-none"
                        aria-label="수량"
                      />
                      <button
                        type="button"
                        className="h-8 w-8 flex items-center justify-center text-muted-foreground hover:text-foreground"
                        onClick={() => opt.changeNoOptionQuantity(1)}
                      >
                        <Plus className="h-3 w-3" />
                      </button>
                    </div>
                    {!isAuthenticated && (
                      <span className="ml-3 text-[11px] text-muted-foreground">
                        로그인 후 구매가능
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* 필수 옵션 드롭다운 */}
              {opt.hasRequiredOptions && (
                <div className="space-y-3">
                  {opt.requiredOptions.map((option) => (
                    <div key={option.id}>
                      <label className="text-[13px] text-foreground mb-1 block">
                        {option.name}
                        <span className="text-destructive ml-0.5">*</span>
                      </label>
                      <OptionSelect
                        option={option}
                        value={opt.currentSelections[option.id] ?? ""}
                        onValueChange={(val) => opt.selectVariant(option.id, val)}
                        showAddedPrice
                        resetKey={opt.selectedItems.length}
                        disabledReasons={opt.disabledVariantReasons}
                      />
                    </div>
                  ))}
                </div>
              )}

              {/* 선택 옵션 드롭다운 */}
              {opt.hasOptionalOptions && (
                <>
                  <Separator />
                  <p className="text-[12px] text-muted-foreground">추가 선택</p>
                  {opt.optionalOptions.map((option) => (
                    <div key={option.id}>
                      <label className="text-[13px] text-foreground mb-1 block">
                        {option.name}
                      </label>
                      <OptionSelect
                        option={option}
                        value=""
                        onValueChange={(val) => handleOptionalAdd(option.id, val)}
                        resetKey={`opt-${option.id}-${opt.selectedItems.length}`}
                        disabledReasons={opt.disabledVariantReasons}
                      />
                    </div>
                  ))}
                </>
              )}

              {/* 선택된 옵션 블록 */}
              {opt.selectedItems.length > 0 && (
                <div className="mt-4 space-y-2">
                  {opt.selectedItems.map((item) => (
                    <SelectedOptionBlock
                      key={item.id}
                      item={item}
                      isAuthenticated={isAuthenticated}
                      onQuantityChange={opt.changeItemQuantity}
                      onQuantitySet={opt.setItemQuantity}
                      onRemove={opt.removeItem}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* 옵션 없는 경우: 수량만 */}
          {!opt.hasOptions && (
            <div className="flex items-center justify-between mt-5">
              <span className="text-[13px] text-muted-foreground">수량</span>
              <div className="flex items-center">
                <div className="flex items-center border border-border">
                  <button
                    type="button"
                    className="h-8 w-8 flex items-center justify-center text-muted-foreground hover:text-foreground"
                    onClick={() => opt.changeNoOptionQuantity(-1)}
                    disabled={opt.noOptionQuantity <= opt.noOptionMin}
                  >
                    <Minus className="h-3 w-3" />
                  </button>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={opt.noOptionQuantity}
                    onChange={(e) => {
                      const raw = e.target.value.replace(/[^0-9]/g, "");
                      if (raw === "") return;
                      opt.setNoOptionQuantityDirect(parseInt(raw, 10));
                    }}
                    onBlur={(e) => {
                      const raw = e.target.value.replace(/[^0-9]/g, "");
                      const parsed =
                        raw === "" ? opt.noOptionMin : Math.max(opt.noOptionMin, parseInt(raw, 10));
                      opt.setNoOptionQuantityDirect(parsed);
                    }}
                    className="w-10 h-8 text-center text-[13px] bg-transparent focus:outline-none"
                    aria-label="수량"
                  />
                  <button
                    type="button"
                    className="h-8 w-8 flex items-center justify-center text-muted-foreground hover:text-foreground"
                    onClick={() => opt.changeNoOptionQuantity(1)}
                  >
                    <Plus className="h-3 w-3" />
                  </button>
                </div>
                {!isAuthenticated && (
                  <span className="ml-3 text-[11px] text-muted-foreground">로그인 후 구매가능</span>
                )}
              </div>
            </div>
          )}

          {/* 총 금액 */}
          <div className="flex items-center justify-between mt-5 pt-4 border-t border-border">
            <span className="text-[13px] text-muted-foreground">총 상품금액</span>
            {isAuthenticated ? (
              <span className="text-lg font-bold tracking-tight">
                {(flashSaleInfo?.discountedPrice ?? opt.totalPrice).toLocaleString("ko-KR")}원
              </span>
            ) : (
              <span className="text-[13px] text-muted-foreground">로그인 후 가격확인</span>
            )}
          </div>

          {/* CTA */}
          <div className="mt-4">
            {flashSaleInfo ? (
              <button
                type="button"
                className="w-full h-12 rounded-sm bg-neutral-900 text-white text-[14px] font-medium hover:bg-neutral-800 transition-colors"
                onClick={() => router.push(`/flash-sale/${flashSaleInfo.slug}`)}
              >
                깜짝할인에서 구매하기
              </button>
            ) : unavailableLabel ? (
              <button
                type="button"
                className="w-full h-12 rounded-sm bg-neutral-300 text-white text-[14px] font-medium cursor-not-allowed"
                disabled
              >
                {unavailableLabel}
              </button>
            ) : isAuthenticated ? (
              <button
                type="button"
                className="w-full h-12 rounded-sm bg-neutral-900 text-white text-[14px] font-medium hover:bg-neutral-800 transition-colors disabled:bg-neutral-300"
                onClick={handleAddToCart}
                disabled={addToCart.isPending || !opt.canAddToCart}
              >
                {addToCart.isPending ? "추가 중..." : "장바구니 추가"}
              </button>
            ) : (
              <Link
                href="/login"
                className="flex items-center justify-center w-full h-12 rounded-sm bg-neutral-900 text-white text-[14px] font-medium hover:bg-neutral-800 transition-colors"
              >
                장바구니 추가
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* ── 판매자 공지 인라인 알림 (클릭 시 탭 전환) ── */}
      {brandNotice && (brandNotice.title || brandNoticeHtml) && (
        <button
          type="button"
          onClick={() => setActiveTab("판매자 공지")}
          className="group mt-10 w-full flex items-center justify-between gap-4 border border-neutral-300 bg-neutral-50 px-5 py-4 text-left hover:border-neutral-900 hover:bg-neutral-100 transition-colors"
        >
          <span className="flex flex-col min-w-0">
            <span className="text-[12px] font-medium text-neutral-500">판매자 공지사항</span>
            <span className="text-[15px] font-medium text-foreground truncate mt-0.5">
              {brandNotice.title?.trim() || "판매자 공지사항이 있어요"}
            </span>
          </span>
          <span className="flex shrink-0 items-center gap-1 rounded-sm border border-neutral-900 bg-white px-4 py-2 text-[13px] font-medium text-neutral-900 group-hover:bg-neutral-900 group-hover:text-white transition-colors">
            상세보기
            <ChevronRight className="h-3.5 w-3.5" />
          </span>
        </button>
      )}

      {/* ── 탭 ── */}
      <div className="mt-12">
        <ProductInfoTabs
          content={product.content}
          expanded={contentExpanded}
          onToggleExpanded={setContentExpanded}
          brandNoticeHtml={brandNoticeHtml || undefined}
          brandNoticeTitle={brandNotice?.title}
          brandNoticeIsHtml={brandNotice?.isHtmlContent}
          value={activeTab}
          onValueChange={setActiveTab}
        />
      </div>

      {/* ── 같은 브랜드의 다른 상품 ── */}
      {relatedProducts.length > 0 && (
        <div className="mt-16">
          <h2 className="text-[15px] font-medium mb-5">같은 브랜드의 다른 상품</h2>
          <ScrollArea className="w-full">
            <div className="flex gap-4 pb-4">
              {relatedProducts.map((rp) => (
                <ProductCard key={rp.id} product={rp} className="w-48 shrink-0" />
              ))}
            </div>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>
        </div>
      )}

      {/* 장바구니 성공 다이얼로그 */}
      <AlertDialog open={cartDialogOpen} onOpenChange={setCartDialogOpen}>
        <AlertDialogContent className="sm:rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>장바구니에 추가되었습니다</AlertDialogTitle>
            <AlertDialogDescription>장바구니로 이동하시겠습니까?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>쇼핑 계속</AlertDialogCancel>
            <AlertDialogAction onClick={() => router.push("/cart")}>
              장바구니 보기
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 비로그인 찜 → 로그인 유도 */}
      <AlertDialog open={loginDialogOpen} onOpenChange={setLoginDialogOpen}>
        <AlertDialogContent className="sm:rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>로그인이 필요합니다</AlertDialogTitle>
            <AlertDialogDescription>찜 기능은 로그인 후 이용할 수 있습니다.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>취소</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                router.push(`/login?returnUrl=${encodeURIComponent(`/product/${productId}`)}`)
              }
            >
              로그인
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 온라인 전용 사업자 구매 차단 (레거시 alert 대체) */}
      <AlertDialog open={onlineBlockDialogOpen} onOpenChange={setOnlineBlockDialogOpen}>
        <AlertDialogContent className="sm:rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>구매 불가</AlertDialogTitle>
            <AlertDialogDescription>
              온라인만 운영하는 사업자는 해당 제품을 구매할 수 없습니다.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setOnlineBlockDialogOpen(false)}>
              확인
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function decodeHtmlEntities(input: string): string {
  return input
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, "&");
}

// Legacy brand notices sometimes contain malformed HTML where image attributes
// leak outside the <img> tag and render as visible text (e.g. "alt=..." "style=...").
// Only sanitize text regions between tags so valid attributes inside tags are preserved.
function sanitizeBrandNoticeHtml(input: string): string {
  const ATTR_NAMES = "alt|style|src|class|id|title|width|height|srcset|sizes|loading|decoding";
  const attrRe = new RegExp(`\\b(${ATTR_NAMES})\\s*=\\s*("[^"]*"|'[^']*')\\s*/?`, "gi");
  return input
    .split(/(<[^>]*>)/g)
    .map((part) => {
      if (part.startsWith("<")) return part;
      return part
        .replace(attrRe, "")
        .replace(/\s*>\s*/g, " ")
        .replace(/[ \t]{2,}/g, " ");
    })
    .join("");
}

// ── 내부: 선택된 옵션 블록 ──

function SelectedOptionBlock({
  item,
  isAuthenticated,
  onQuantityChange,
  onQuantitySet,
  onRemove,
}: {
  item: SelectedOptionItem;
  isAuthenticated: boolean;
  onQuantityChange: (id: string, delta: number) => void;
  onQuantitySet: (id: string, quantity: number) => void;
  onRemove: (id: string) => void;
}) {
  return (
    <div className="bg-neutral-50 border border-neutral-200 p-3">
      <div className="flex items-start justify-between gap-2">
        <span className="text-[13px] text-foreground leading-snug">{item.label}</span>
        <button
          type="button"
          onClick={() => onRemove(item.id)}
          className="shrink-0 text-neutral-400 hover:text-neutral-800"
          aria-label="삭제"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="flex items-center justify-between mt-2">
        <div className="flex items-center border border-neutral-300">
          <button
            type="button"
            className="h-7 w-7 flex items-center justify-center text-neutral-500 hover:text-foreground"
            onClick={() => onQuantityChange(item.id, -1)}
            disabled={item.quantity <= 1}
          >
            <Minus className="h-2.5 w-2.5" />
          </button>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={item.quantity}
            onChange={(e) => {
              const raw = e.target.value.replace(/[^0-9]/g, "");
              if (raw === "") return;
              onQuantitySet(item.id, parseInt(raw, 10));
            }}
            onBlur={(e) => {
              const raw = e.target.value.replace(/[^0-9]/g, "");
              const parsed = raw === "" ? 1 : Math.max(1, parseInt(raw, 10));
              onQuantitySet(item.id, parsed);
            }}
            className="w-8 h-7 text-center text-[12px] bg-transparent focus:outline-none"
            aria-label="수량"
          />
          <button
            type="button"
            className="h-7 w-7 flex items-center justify-center text-neutral-500 hover:text-foreground"
            onClick={() => onQuantityChange(item.id, 1)}
          >
            <Plus className="h-2.5 w-2.5" />
          </button>
        </div>
        {isAuthenticated ? (
          <span className="text-[13px] font-medium">
            {(item.price * item.quantity).toLocaleString("ko-KR")}원
          </span>
        ) : (
          <span className="text-[11px] text-muted-foreground">로그인 후 가격확인</span>
        )}
      </div>
    </div>
  );
}
